import React from 'react';
import { restaurantConfig } from '../config/restaurantConfig';
import { Link } from 'react-router-dom';
// Brand logo + complaint book image
import logoImg from '../assets/images/logo.webp';
import reclamaImg from '../assets/images/reclama.png';

interface FooterProps {
  language: string;
}

export const Footer = ({ language }: FooterProps) => {
  const { contact } = restaurantConfig;
  const currentYear = new Date().getFullYear();
  const directionsUrl =
    'https://www.google.com/maps/dir/?api=1&destination=' +
    encodeURIComponent(contact.address.displayAddress);

  const Heading = ({ children }: { children: React.ReactNode }) => (
    <div className="mb-5">
      <h3 className="text-white text-lg font-bold tracking-wide">{children}</h3>
      <span className="rc-foot-rule mt-2 block" />
    </div>
  );

  return (
    <footer
      className="relative overflow-hidden py-16"
      style={{ background: 'linear-gradient(180deg, #6f1617 0%, #561011 100%)' }}
    >
      {/* gold hairline at the very top */}
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,175,55,.6), transparent)' }} />

      <div className="mx-auto w-full px-5" style={{ maxWidth: '1200px' }}>
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          {/* Column 1 — brand */}
          <div>
            <img src={logoImg} alt="Royal Indian Cuisine" className="h-20 w-auto object-contain" />
            <p className="mt-4 text-sm leading-relaxed text-white/75">
              {language === 'en'
                ? 'Authentic Tandoori cooking & traditional Indian cuisine in the heart of Lagos, Algarve — since 2023.'
                : 'Cozinha Tandoori autêntica e cozinha indiana tradicional no coração de Lagos, Algarve — desde 2023.'}
            </p>
            <a
              href={`tel:${contact.phone.dialing}`}
              className="mt-5 inline-flex items-center gap-3 rounded-xl border border-[#d4af37]/40 bg-white/5 px-4 py-3 transition hover:bg-white/10"
            >
              <i className="fas fa-phone-alt text-[#e7d3a1]" />
              <span>
                <span className="block text-[0.7rem] uppercase tracking-widest text-white/60">
                  {language === 'en' ? 'Reservations' : 'Reservas'}
                </span>
                <span className="block text-base font-bold text-white">{contact.phone.display}</span>
              </span>
            </a>
          </div>

          {/* Column 2 — contact */}
          <div>
            <Heading>{language === 'en' ? 'Visit Us' : 'Visite-nos'}</Heading>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <i className="fas fa-map-marker-alt mt-1 w-4 text-center text-[#e7d3a1]" />
                <span className="text-white/85">{contact.address.displayAddress}</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="fas fa-clock mt-1 w-4 text-center text-[#e7d3a1]" />
                <span className="text-white/85">{language === 'en' ? 'Open daily · 11 AM – 10:30 PM' : 'Aberto diariamente · 11h – 22h30'}</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="fas fa-envelope mt-1 w-4 text-center text-[#e7d3a1]" />
                <a href={`mailto:${contact.email}`} className="break-all text-white/85 transition hover:text-white">{contact.email}</a>
              </li>
            </ul>
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/50 px-5 py-2 text-sm font-semibold text-[#e7d3a1] transition hover:bg-[#d4af37]/15"
            >
              <i className="fas fa-location-arrow" /> {language === 'en' ? 'Get directions' : 'Como chegar'}
            </a>
          </div>

          {/* Column 3 — quick links */}
          <div>
            <Heading>{language === 'en' ? 'Explore' : 'Explorar'}</Heading>
            <ul className="space-y-3 text-sm">
              {[
                { to: '/', en: 'Home', pt: 'Início' },
                { to: '/menu-full', en: 'Menu', pt: 'Menu' },
                { to: '/#about', en: 'About Us', pt: 'Sobre Nós' },
                { to: '/#gallery', en: 'Gallery', pt: 'Galeria' },
                { to: '/#contact', en: 'Contact', pt: 'Contacto' },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="group inline-flex items-center gap-2 text-white/80 transition hover:text-white"
                  >
                    <i className="fas fa-chevron-right text-[0.6rem] text-[#d4af37] transition group-hover:translate-x-0.5" />
                    {language === 'en' ? l.en : l.pt}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 — connect + newsletter */}
          <div>
            <Heading>{language === 'en' ? 'Stay in Touch' : 'Fique a Par'}</Heading>
            <div className="flex gap-3">
              {restaurantConfig.social?.instagram && (
                <a href={restaurantConfig.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:border-[#d4af37]/50 hover:text-[#e7d3a1]">
                  <i className="fab fa-instagram" />
                </a>
              )}
              {restaurantConfig.social?.facebook && (
                <a href={restaurantConfig.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:border-[#d4af37]/50 hover:text-[#e7d3a1]">
                  <i className="fab fa-facebook-f" />
                </a>
              )}
              <a href={`https://wa.me/${contact.phone.dialing.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:border-[#25D366]/60 hover:text-[#25D366]">
                <i className="fab fa-whatsapp" />
              </a>
            </div>

            {/* Complaint book */}
            <a
              href="https://www.livroreclamacoes.pt"
              target="_blank"
              rel="noopener noreferrer"
              title={language === 'en' ? 'Complaint Book' : 'Livro de Reclamações'}
              className="mt-5 inline-block rounded-md bg-white px-3 py-2 transition-transform hover:scale-105"
            >
              <img src={reclamaImg} alt={language === 'en' ? 'Complaint Book' : 'Livro de Reclamações'} className="max-h-9" />
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-col items-center gap-4 border-t border-white/10 pt-6 text-sm md:flex-row md:justify-between">
          <p className="text-center text-white/65 md:text-left">
            © {currentYear} {restaurantConfig.name}. {language === 'en' ? 'All rights reserved.' : 'Todos os direitos reservados.'}
          </p>
          <a
            href="https://www.nexosolutions.co"
            target="_blank"
            rel="noopener noreferrer"
            className="rc-nexo-credit text-sm"
          >
            Website demo criado por Nexo Solutions
          </a>
        </div>
      </div>

      {/* Back to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="absolute bottom-6 right-6 flex h-12 w-12 items-center justify-center rounded-full border border-[#d4af37]/40 bg-white/5 text-[#e7d3a1] transition hover:-translate-y-1 hover:bg-white/10"
        aria-label={language === 'en' ? 'Back to top' : 'Voltar ao topo'}
      >
        <i className="fas fa-chevron-up" />
      </button>
    </footer>
  );
};

export default Footer;
